import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const InvoicingPayments = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [financing, setFinancing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('schedule'); // schedule, history, escrow, invoices

  // Check if user is authorized
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn) {
      navigate('/signin');
    }
  }, [navigate]);

  // Mock data for disbursed financing
  useEffect(() => {
    setTimeout(() => {
      const mockData = {
        1: {
          id: 1,
          businessName: 'Tech Startup LLC',
          contactName: 'Ahmed Khan',
          email: 'ahmed@techstartup.com',
          phone: '+1-555-0123',
          financingAmount: 250000,
          financingType: 'Murabaha',
          profitRate: 8.5,
          term: 36,
          monthlyPayment: 7891.45,
          disbursementDate: '2024-01-20',
          maturityDate: '2027-01-20',
          status: 'Active',
          
          paymentSchedule: [
            { month: 1, dueDate: '2024-02-20', principal: 6141.45, profit: 1750.00, total: 7891.45, status: 'Paid', paidDate: '2024-02-19', paidAmount: 7891.45 },
            { month: 2, dueDate: '2024-03-20', principal: 6184.92, profit: 1706.53, total: 7891.45, status: 'Paid', paidDate: '2024-03-20', paidAmount: 7891.45 },
            { month: 3, dueDate: '2024-04-20', principal: 6228.70, profit: 1662.75, total: 7891.45, status: 'Paid', paidDate: '2024-04-18', paidAmount: 7891.45 },
            { month: 4, dueDate: '2024-05-20', principal: 6272.79, profit: 1618.66, total: 7891.45, status: 'Overdue', paidDate: null, paidAmount: 0, daysOverdue: 5 },
            { month: 5, dueDate: '2024-06-20', principal: 6317.19, profit: 1574.26, total: 7891.45, status: 'Upcoming', paidDate: null, paidAmount: 0 },
            { month: 6, dueDate: '2024-07-20', principal: 6361.91, profit: 1529.54, total: 7891.45, status: 'Scheduled', paidDate: null, paidAmount: 0 },
          ],

          escrow: {
            currentBalance: 15000,
            requiredBalance: 12000,
            status: 'Healthy',
            items: [
              { type: 'Property Tax', amount: 5000, nextDueDate: '2024-06-15', frequency: 'Quarterly' },
              { type: 'Insurance Premium', amount: 4000, nextDueDate: '2024-08-01', frequency: 'Annual' },
              { type: 'Maintenance Reserve', amount: 3000, nextDueDate: 'N/A', frequency: 'Monthly' }
            ],
            transactions: [
              { date: '2024-05-15', description: 'Property Tax Payment', amount: -5000, balance: 15000 },
              { date: '2024-04-20', description: 'Monthly Deposit', amount: 500, balance: 20000 },
              { date: '2024-03-20', description: 'Monthly Deposit', amount: 500, balance: 19500 },
            ]
          },

          paymentHistory: [
            { date: '2024-04-18', invoiceNumber: 'INV-2024-004', type: 'Regular Payment', principal: 6228.70, profit: 1662.75, lateFee: 0, total: 7891.45, method: 'Bank Transfer', status: 'Cleared', confirmationNumber: 'TXN-789456123' },
            { date: '2024-03-20', invoiceNumber: 'INV-2024-003', type: 'Regular Payment', principal: 6184.92, profit: 1706.53, lateFee: 0, total: 7891.45, method: 'ACH', status: 'Cleared', confirmationNumber: 'TXN-789456122' },
            { date: '2024-02-19', invoiceNumber: 'INV-2024-002', type: 'Regular Payment', principal: 6141.45, profit: 1750.00, lateFee: 0, total: 7891.45, method: 'Bank Transfer', status: 'Cleared', confirmationNumber: 'TXN-789456121' },
            { date: '2024-01-20', invoiceNumber: 'INV-2024-001', type: 'Disbursement', principal: 0, profit: 0, lateFee: 0, total: 250000, method: 'Wire Transfer', status: 'Cleared', confirmationNumber: 'TXN-789456120' }
          ],

          invoices: [
            { invoiceNumber: 'INV-2024-005', issueDate: '2024-05-01', dueDate: '2024-05-20', amount: 7891.45, status: 'Overdue', daysOverdue: 5, items: [{ description: 'Principal Payment', amount: 6272.79 }, { description: 'Profit Payment', amount: 1618.66 }] },
            { invoiceNumber: 'INV-2024-006', issueDate: '2024-06-01', dueDate: '2024-06-20', amount: 7891.45, status: 'Pending', daysOverdue: 0, items: [{ description: 'Principal Payment', amount: 6317.19 }, { description: 'Profit Payment', amount: 1574.26 }] }
          ]
        },
        3: {
          id: 3,
          businessName: 'Halal Foods Inc',
          contactName: 'Omar Hassan',
          email: 'omar@halalfoods.com',
          phone: '+1-555-0125',
          financingAmount: 100000,
          financingType: 'Ijara',
          profitRate: 7.0,
          term: 24,
          monthlyPayment: 4544.73,
          disbursementDate: '2023-10-15',
          maturityDate: '2025-10-15',
          status: 'Active',
          
          paymentSchedule: [
            { month: 1, dueDate: '2023-11-15', principal: 3961.40, profit: 583.33, total: 4544.73, status: 'Paid', paidDate: '2023-11-14', paidAmount: 4544.73 },
            { month: 2, dueDate: '2023-12-15', principal: 3984.50, profit: 560.23, total: 4544.73, status: 'Paid', paidDate: '2023-12-15', paidAmount: 4544.73 },
            { month: 3, dueDate: '2024-01-15', principal: 4007.74, profit: 536.99, total: 4544.73, status: 'Paid', paidDate: '2024-01-15', paidAmount: 4544.73 },
            { month: 4, dueDate: '2024-02-15', principal: 4031.12, profit: 513.61, total: 4544.73, status: 'Paid', paidDate: '2024-02-14', paidAmount: 4544.73 },
            { month: 5, dueDate: '2024-03-15', principal: 4054.63, profit: 490.10, total: 4544.73, status: 'Paid', paidDate: '2024-03-15', paidAmount: 4544.73 },
            { month: 6, dueDate: '2024-04-15', principal: 4078.29, profit: 466.44, total: 4544.73, status: 'Paid', paidDate: '2024-04-15', paidAmount: 4544.73 },
            { month: 7, dueDate: '2024-05-15', principal: 4102.08, profit: 442.65, total: 4544.73, status: 'Paid', paidDate: '2024-05-15', paidAmount: 4544.73 },
            { month: 8, dueDate: '2024-06-15', principal: 4126.01, profit: 418.72, total: 4544.73, status: 'Upcoming', paidDate: null, paidAmount: 0 },
            { month: 9, dueDate: '2024-07-15', principal: 4150.09, profit: 394.64, total: 4544.73, status: 'Scheduled', paidDate: null, paidAmount: 0 },
            { month: 10, dueDate: '2024-08-15', principal: 4174.31, profit: 370.42, total: 4544.73, status: 'Scheduled', paidDate: null, paidAmount: 0 },
          ],

          escrow: {
            currentBalance: 8500,
            requiredBalance: 7200,
            status: 'Healthy',
            items: [
              { type: 'Property Insurance', amount: 3600, nextDueDate: '2024-10-15', frequency: 'Annual' },
              { type: 'Equipment Insurance', amount: 2400, nextDueDate: '2024-07-01', frequency: 'Semi-Annual' },
              { type: 'Property Tax Reserve', amount: 1200, nextDueDate: '2024-12-31', frequency: 'Annual' }
            ],
            transactions: [
              { date: '2024-05-15', description: 'Monthly Escrow Deposit', amount: 300, balance: 8500 },
              { date: '2024-04-15', description: 'Monthly Escrow Deposit', amount: 300, balance: 8200 },
              { date: '2024-04-01', description: 'Equipment Insurance Payment', amount: -1200, balance: 7900 },
              { date: '2024-03-15', description: 'Monthly Escrow Deposit', amount: 300, balance: 9100 },
            ]
          },

          paymentHistory: [
            { date: '2024-05-15', invoiceNumber: 'INV-HF-007', type: 'Regular Payment', principal: 4102.08, profit: 442.65, lateFee: 0, total: 4544.73, method: 'ACH', status: 'Cleared', confirmationNumber: 'TXN-HF-007' },
            { date: '2024-04-15', invoiceNumber: 'INV-HF-006', type: 'Regular Payment', principal: 4078.29, profit: 466.44, lateFee: 0, total: 4544.73, method: 'ACH', status: 'Cleared', confirmationNumber: 'TXN-HF-006' },
            { date: '2024-03-15', invoiceNumber: 'INV-HF-005', type: 'Regular Payment', principal: 4054.63, profit: 490.10, lateFee: 0, total: 4544.73, method: 'ACH', status: 'Cleared', confirmationNumber: 'TXN-HF-005' },
            { date: '2024-02-14', invoiceNumber: 'INV-HF-004', type: 'Regular Payment', principal: 4031.12, profit: 513.61, lateFee: 0, total: 4544.73, method: 'ACH', status: 'Cleared', confirmationNumber: 'TXN-HF-004' },
            { date: '2024-01-15', invoiceNumber: 'INV-HF-003', type: 'Regular Payment', principal: 4007.74, profit: 536.99, lateFee: 0, total: 4544.73, method: 'ACH', status: 'Cleared', confirmationNumber: 'TXN-HF-003' },
            { date: '2023-12-15', invoiceNumber: 'INV-HF-002', type: 'Regular Payment', principal: 3984.50, profit: 560.23, lateFee: 0, total: 4544.73, method: 'ACH', status: 'Cleared', confirmationNumber: 'TXN-HF-002' },
            { date: '2023-11-14', invoiceNumber: 'INV-HF-001', type: 'Regular Payment', principal: 3961.40, profit: 583.33, lateFee: 0, total: 4544.73, method: 'ACH', status: 'Cleared', confirmationNumber: 'TXN-HF-001' },
            { date: '2023-10-15', invoiceNumber: 'INV-HF-000', type: 'Disbursement', principal: 0, profit: 0, lateFee: 0, total: 100000, method: 'Wire Transfer', status: 'Cleared', confirmationNumber: 'TXN-HF-000' }
          ],

          invoices: [
            { invoiceNumber: 'INV-HF-008', issueDate: '2024-06-01', dueDate: '2024-06-15', amount: 4544.73, status: 'Pending', daysOverdue: 0, items: [{ description: 'Principal Payment', amount: 4126.01 }, { description: 'Profit Payment', amount: 418.72 }] }
          ]
        }
      };

      setFinancing(mockData[id] || null);
      setIsLoading(false);
    }, 500);
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (!financing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Financing not found</p>
          <Button onClick={() => navigate('/crm/admin')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Calculate summary statistics
  const totalPaid = financing.paymentHistory
    .filter(p => p.type === 'Regular Payment' && p.status === 'Cleared')
    .reduce((sum, p) => sum + p.total, 0);
  
  const paidPayments = financing.paymentSchedule.filter(p => p.status === 'Paid').length;
  const remainingPayments = financing.term - paidPayments;
  const remainingBalance = financing.financingAmount - financing.paymentSchedule
    .filter(p => p.status === 'Paid')
    .reduce((sum, p) => sum + p.principal, 0);
  
  const overduePayments = financing.paymentSchedule.filter(p => p.status === 'Overdue');
  const overdueAmount = overduePayments.reduce((sum, p) => sum + p.total, 0);

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'Upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'Scheduled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/crm/admin')}
              className="mr-4"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Invoicing & Payments</h1>
              <p className="text-sm text-gray-600">{financing.businessName} - {financing.financingType}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Statement
              </Button>
              <Button>
                Generate Invoice
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Total Financing</p>
                <p className="text-2xl font-bold text-gray-900">${financing.financingAmount.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">{financing.term} months @ {financing.profitRate}%</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Remaining Balance</p>
                <p className="text-2xl font-bold text-gray-900">${remainingBalance.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">{remainingPayments} payments left</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Total Paid</p>
                <p className="text-2xl font-bold text-green-600">${totalPaid.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">{paidPayments} payments completed</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div>
                <p className="text-sm text-gray-600">Overdue Amount</p>
                <p className="text-2xl font-bold text-red-600">${overdueAmount.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">{overduePayments.length} overdue payment(s)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'schedule', name: 'Payment Schedule' },
                { id: 'history', name: 'Payment History' },
                { id: 'escrow', name: 'Escrow Management' },
                { id: 'invoices', name: 'Outstanding Invoices' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`${
                    selectedTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Payment Schedule Tab */}
        {selectedTab === 'schedule' && (
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Payment Schedule</h2>
              <p className="text-sm text-gray-600">Monthly: ${financing.monthlyPayment.toLocaleString()}</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Principal</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {financing.paymentSchedule.map((payment) => (
                      <tr key={payment.month} className={payment.status === 'Overdue' ? 'bg-red-50' : ''}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{payment.month}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{payment.dueDate}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">${payment.principal.toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">${payment.profit.toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">${payment.total.toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge className={getPaymentStatusColor(payment.status)}>
                            {payment.status}
                            {payment.status === 'Overdue' && ` (${payment.daysOverdue}d)`}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {payment.paidDate || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment History Tab */}
        {selectedTab === 'history' && (
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Payment History</h2>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Principal</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Late Fee</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {financing.paymentHistory.map((payment, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{payment.date}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">{payment.invoiceNumber}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{payment.type}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">${payment.principal.toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">${payment.profit.toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">${payment.lateFee.toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">${payment.total.toLocaleString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{payment.method}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge className="bg-green-100 text-green-800">{payment.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Escrow Management Tab */}
        {selectedTab === 'escrow' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold">Escrow Account Summary</h2>
                    <p className="text-sm text-gray-600">Current Balance: ${financing.escrow.currentBalance.toLocaleString()}</p>
                  </div>
                  <Badge className={
                    financing.escrow.status === 'Healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }>
                    {financing.escrow.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {financing.escrow.items.map((item, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-900">{item.type}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-2">${item.amount.toLocaleString()}</p>
                      <p className="text-xs text-gray-600 mt-1">Next Due: {item.nextDueDate}</p>
                      <p className="text-xs text-gray-500">Frequency: {item.frequency}</p>
                    </div>
                  ))}
                </div>

                <h3 className="text-md font-semibold mb-4">Recent Transactions</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {financing.escrow.transactions.map((txn, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{txn.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{txn.description}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${
                            txn.amount > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {txn.amount > 0 ? '+' : ''}${Math.abs(txn.amount).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">${txn.balance.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Outstanding Invoices Tab */}
        {selectedTab === 'invoices' && (
          <div className="space-y-6">
            {financing.invoices.map((invoice) => (
              <Card key={invoice.invoiceNumber}>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold">{invoice.invoiceNumber}</h3>
                      <p className="text-sm text-gray-600">Issued: {invoice.issueDate} | Due: {invoice.dueDate}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }>
                        {invoice.status}
                        {invoice.daysOverdue > 0 && ` (${invoice.daysOverdue} days)`}
                      </Badge>
                      <p className="text-2xl font-bold text-gray-900 mt-2">${invoice.amount.toLocaleString()}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {invoice.items.map((item, index) => (
                      <div key={index} className="flex justify-between py-2 border-b">
                        <span className="text-sm text-gray-600">{item.description}</span>
                        <span className="text-sm font-medium text-gray-900">${item.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline">View PDF</Button>
                    <Button variant="outline">Send Reminder</Button>
                    <Button>Record Payment</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicingPayments;

